import { AppColors, DateDisplay, EnumValueDisplay } from '@lib/common';
import {
  Box,
  createStyles,
  Grid,
  LinearProgress,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  corporateAccountService,
  Employee,
  InvoiceRun,
  invoiceRunService,
  InvoiceStatus,
} from 'app/state';
import React, { useCallback, useEffect, useMemo } from 'react';
import { InvoiceRunDownloadButton } from './invoice-run-download-button';
import { InvoiceRunStatus } from '@lib/state/api/generated/reservations';

interface Props {
  run: InvoiceRun;
  employees: Array<Employee>;
  invoiceStatuses: Array<InvoiceStatus>;
}

export const useStyles = makeStyles(theme =>
  createStyles({
    runDetailText: {
      fontSize: '1.5em',
    },
    runDetailCells: {
      display: 'flex',
    },
    draft: {
      display: 'flex',
      textAlign: 'right',
      backgroundColor: AppColors.DarkBlue,
      color: AppColors.White,
      padding: '.5rem 1rem',
      borderRadius: '1rem',
    },
    downloadButton: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      textAlign: 'right',
      justifyItems: 'flex-end',
      alignContent: 'flex-end',
    },
  })
);

export function InvoiceRunDetails({ run, employees, invoiceStatuses }: Props) {
  const styles = useStyles();

  useEffect(() => {
    corporateAccountService.setSelectedTab('runList');
  }, []);

  const employee = useMemo(() => employees.find(e => e.userId === run.createdById), [
    run,
    employees,
  ]);

  const completedInvoices = useMemo(
    () =>
      invoiceStatuses.filter(
        s =>
          s.createInvoiceStatus === 'Success' ||
          s.createInvoiceStatus === 'SuccessWithWarnings' ||
          s.createInvoiceStatus === 'Failed'
      ),
    [invoiceStatuses]
  );

  const exportRun = useCallback(() => {
    invoiceRunService.downloadExport(run);
  }, [run]);

  const generateExport = useCallback(() => {
    invoiceRunService.generateExport(run.id!);
  }, [run]);

  if (!run) return null;

  return (
    <Box>
      {invoiceStatuses.length > 0 && run.runStatus !== InvoiceRunStatus.Complete && (
        <Tooltip title={`${completedInvoices.length}/${invoiceStatuses.length} invoices generated`}>
          <LinearProgress
            variant="determinate"
            value={(completedInvoices.length / invoiceStatuses.length) * 100}
          />
        </Tooltip>
      )}
      <Grid container justifyContent="space-between" style={{ marginTop: 0 }} spacing={1}>
        <Grid item sm={6} className={styles.runDetailCells}>
          <Typography variant="h1">Invoice run for:&ensp;</Typography>
          <DateDisplay date={new Date(run.createdDate ?? '')} dateFormat="M/d/yy" variant="h1" />
        </Grid>
        {run.isDraft && (
          <Grid container item sm={6} className={styles.runDetailCells} justifyContent="flex-end">
            <Typography variant="h4" className={styles.draft}>
              Draft
            </Typography>
          </Grid>
        )}

        <Grid item sm={12} className={styles.runDetailCells}>
          <Grid
            item
            sm={6}
            className={styles.runDetailCells}
            style={{ alignItems: 'center', flexDirection: 'row' }}
          >
            <Typography variant="h2">Status:&ensp;</Typography>
            <EnumValueDisplay value={run.runStatus} variant="h2" />
          </Grid>
          <Grid item sm={6} className={styles.downloadButton}>
            {invoiceStatuses.some(s => s.createInvoiceStatus !== 'Failed') && (
              <InvoiceRunDownloadButton
                draft={run.isDraft ?? false}
                exportGenerated={run.exportGenerated ?? false}
                generatingExport={run.generatingExports ?? false}
                onDownloadExport={exportRun}
                onGenerateExport={generateExport}
                runStatus={run.runStatus}
              />
            )}
          </Grid>
        </Grid>
        <Grid item sm={6} className={styles.runDetailCells}>
          <Typography className={styles.runDetailText}>Start Date:&ensp;</Typography>{' '}
          <DateDisplay
            className={styles.runDetailText}
            date={new Date(run.invoiceStartDate ?? '')}
            dateFormat="M/d/yy"
          />
        </Grid>
        <Grid item sm={6} className={styles.runDetailCells}>
          <Grid item sm={6} className={styles.runDetailCells}>
            <Typography className={styles.runDetailText}>End Date:&ensp;</Typography>
            <DateDisplay
              className={styles.runDetailText}
              date={new Date(run.invoiceEndDate ?? '')}
              dateFormat="M/d/yy"
            />
          </Grid>
          {/* <Grid item sm={6} className={styles.downloadButton}>
            {run?.id && run.isDraft && (
              <RegenerateAllForm
                runId={run.id}
                disabled={run.runStatus !== InvoiceRunStatus.Complete}
              />
            )}
          </Grid> */}
        </Grid>
        <Grid item sm={6} className={styles.runDetailCells}>
          <Typography className={styles.runDetailText}>Type:&ensp;</Typography>
          <EnumValueDisplay className={styles.runDetailText} value={run.invoiceType} />
        </Grid>
        <Grid item sm={6} className={styles.runDetailCells}>
          <Typography className={styles.runDetailText}>Billing Frequency:&ensp;</Typography>
          {run.billingFrequencyType ? (
            <EnumValueDisplay className={styles.runDetailText} value={run.billingFrequencyType} />
          ) : (
            <Typography className={styles.runDetailText}>N/A</Typography>
          )}
        </Grid>
        <Grid item sm={6} className={styles.runDetailCells}>
          <Typography className={styles.runDetailText}>
            Created By: {employee?.name.first} {employee?.name.last}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
}
